import * as React from 'react';
import type { ToastActionElement, ToastProps } from '@/components/ui/toast';

type ToastRecord = {
  id: string;
  title?: React.ReactNode;
  description?: React.ReactNode;
  action?: ToastActionElement;
} & Omit<ToastProps, 'id'>;

type State = { toasts: ToastRecord[] };

const TOAST_LIMIT = 5;
const TOAST_REMOVE_DELAY = 6_000;

let counter = 0;
function nextId() {
  counter = (counter + 1) % Number.MAX_SAFE_INTEGER;
  return counter.toString();
}

type Action =
  | { type: 'ADD'; toast: ToastRecord }
  | { type: 'UPDATE'; toast: Partial<ToastRecord> & { id: string } }
  | { type: 'DISMISS'; id?: string }
  | { type: 'REMOVE'; id?: string };

const listeners = new Set<(state: State) => void>();
let memory: State = { toasts: [] };
const timeouts = new Map<string, ReturnType<typeof setTimeout>>();

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'ADD':
      return { toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT) };
    case 'UPDATE':
      return {
        toasts: state.toasts.map((t) => (t.id === action.toast.id ? { ...t, ...action.toast } : t)),
      };
    case 'DISMISS': {
      const { id } = action;
      if (id) enqueueRemoval(id);
      else state.toasts.forEach((t) => enqueueRemoval(t.id));
      return {
        toasts: state.toasts.map((t) => (!id || t.id === id ? { ...t, open: false } : t)),
      };
    }
    case 'REMOVE':
      return { toasts: action.id ? state.toasts.filter((t) => t.id !== action.id) : [] };
    default:
      return state;
  }
}

function dispatch(action: Action) {
  memory = reducer(memory, action);
  listeners.forEach((l) => l(memory));
}

function enqueueRemoval(id: string) {
  if (timeouts.has(id)) return;
  const t = setTimeout(() => {
    timeouts.delete(id);
    dispatch({ type: 'REMOVE', id });
  }, TOAST_REMOVE_DELAY);
  timeouts.set(id, t);
}

export interface ToastInput extends Omit<ToastRecord, 'id'> {}

export function toast(input: ToastInput) {
  const id = nextId();
  const update = (next: ToastInput) => dispatch({ type: 'UPDATE', toast: { ...next, id } });
  const dismiss = () => dispatch({ type: 'DISMISS', id });
  dispatch({
    type: 'ADD',
    toast: {
      ...input,
      id,
      open: true,
      onOpenChange: (open) => {
        if (!open) dismiss();
      },
    },
  });
  return { id, update, dismiss };
}

export function useToastState(): State & {
  toast: typeof toast;
  dismiss: (id?: string) => void;
} {
  const [state, setState] = React.useState<State>(memory);
  React.useEffect(() => {
    listeners.add(setState);
    return () => {
      listeners.delete(setState);
    };
  }, []);
  return {
    ...state,
    toast,
    dismiss: (id?: string) => dispatch({ type: 'DISMISS', id }),
  };
}

export function useToast() {
  return {
    toast,
    success: (title: string, description?: string) => toast({ title, description, variant: 'success' }),
    error: (title: string, description?: string) => toast({ title, description, variant: 'destructive' }),
    info: (title: string, description?: string) => toast({ title, description }),
  };
}
